import React from 'react';
import DataRow, { DataRowHeader } from './ui/DataRow';
import Field from './ui/Field';
import Section from './ui/Section';
import Segmented from './ui/Segmented';
import Toggle from './ui/Toggle';

/*
 * Joining up, after the paperwork.
 *
 * An admin approving a membership form is not the end of it. Two things
 * still have to happen before somebody is a member the club would call one:
 * the subscription is paid, and the board signs them off and the card is
 * issued. Between those, an applicant sat in a gap the app had no page for —
 * off the pending-approvals list, because an admin had dealt with them, and
 * indistinguishable from a member of thirty years on the members list.
 *
 * This is that gap, as a list somebody can work down. A row leaves it when
 * both boxes are ticked, and comes back when the member sends a renewal form
 * in — which clears both, because last year's subs are not this year's.
 *
 * The board works it in batches: everybody who has paid goes to the next
 * meeting, and is signed off together. So the list filters down to exactly
 * that group, and the whole group can be ticked at once, on the meeting's
 * date rather than the evening somebody typed it in.
 */

const dateish = (v) => {
  if (!v) return null;
  const d = new Date(v);
  return Number.isNaN(d.getTime())
    ? null
    : d.toLocaleDateString('en-NZ', { day: 'numeric', month: 'short', year: 'numeric' });
};

/* How long they have been waiting, which is the thing that decides who gets
   rung up today. Said in days, because a club chases in days. */
const waitingDays = (v) => {
  if (!v) return null;
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return null;
  const days = Math.floor((Date.now() - d.getTime()) / 86400000);
  return days > 0 ? days : 0;
};

const LONG_WAIT_DAYS = 14;

/* Renewing or joining, told from the record: somebody whose account is
   months older than their latest form was a member before it. Not a stored
   fact — the form's own "renewal" flag is cleared when it is approved — so
   it is an inference, and a two-month gap keeps a slow first application
   from reading as a renewal. */
const RENEWAL_GAP_DAYS = 60;
const isRenewing = (u) => {
  const applied = new Date(u.membershipAppliedAt || 0).getTime();
  const created = new Date(u.createdAt || 0).getTime();
  return !!(applied && created && applied - created > RENEWAL_GAP_DAYS * 86400000);
};

const todayInput = () => {
  const d = new Date();
  const pad = n => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

const STAGES = [
  { value: 'all', label: 'All' },
  { value: 'board', label: 'Ready for the board', title: 'Subs paid, card not yet issued' },
  { value: 'subs', label: 'Subs owed' },
  { value: 'waiting', label: `Waiting ${LONG_WAIT_DAYS}+ days` },
];

const inStage = (u, stage) => {
  if (stage === 'board') return !!u.membershipFeePaidAt && !u.membershipCardIssuedAt;
  if (stage === 'subs') return !u.membershipFeePaidAt;
  if (stage === 'waiting') return (waitingDays(u.membershipApprovedAt) || 0) >= LONG_WAIT_DAYS;
  return true;
};

const distinct = (rows, key) => [...new Set(rows.map(r => r[key]).filter(Boolean))].sort();

const compactSelect = { minHeight: 'var(--gs-control-md)', padding: '0 8px' };

function MembershipIntakeTab({ API, clubId, isSuperAdmin, openProfile, showToast, onMembersChanged }) {
  const [rows, setRows] = React.useState(null);
  const [blocked, setBlocked] = React.useState([]);
  const [unblocking, setUnblocking] = React.useState({});
  const [error, setError] = React.useState('');
  const [saving, setSaving] = React.useState({});

  const [search, setSearch] = React.useState('');
  const [stage, setStage] = React.useState('all');
  const [typeFilter, setTypeFilter] = React.useState('');
  const [playerFilter, setPlayerFilter] = React.useState('');
  const [kindFilter, setKindFilter] = React.useState('');

  const [selected, setSelected] = React.useState(() => new Set());
  const [bulkDate, setBulkDate] = React.useState(todayInput);
  const [bulkBusy, setBulkBusy] = React.useState(false);

  const qs = (isSuperAdmin && clubId) ? `?clubId=${clubId}` : '';

  const load = React.useCallback(() => {
    setError('');
    fetch(`${API}/api/users/membership-intake${qs}`)
      .then(r => (r.ok ? r.json() : Promise.reject(new Error('Could not load the list'))))
      .then(d => setRows(Array.isArray(d) ? d : []))
      .catch(() => { setRows([]); setError('Could not load new members.'); });
    fetch(`${API}/api/users/blocked${qs}`)
      .then(r => (r.ok ? r.json() : []))
      .then(d => setBlocked(Array.isArray(d) ? d : []))
      .catch(() => setBlocked([]));
  }, [API, qs]);

  /* Back to what they were before: verified if the club had approved their
     membership, unverified if not. They then reappear above if they still
     have subs or a card outstanding. */
  const unblock = async (u) => {
    const status = u.membershipApprovedAt ? 'verified' : 'unverified';
    setUnblocking(s => ({ ...s, [u.id]: true }));
    try {
      const r = await fetch(`${API}/api/auth/users/${u.id}/status`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status }),
      });
      const data = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(data.error || 'Could not unblock');
      if (onMembersChanged) onMembersChanged({ [u.id]: { accountStatus: status } });
      showToast(`${u.name} unblocked`, 'green');
      load();
    } catch (err) {
      showToast(err.message || 'Could not unblock', 'red');
    } finally {
      setUnblocking(s => { const n = { ...s }; delete n[u.id]; return n; });
    }
  };

  React.useEffect(() => { load(); }, [load]);

  const all = rows || [];
  const types = distinct(all, 'membershipType');
  const playerTypes = distinct(all, 'playerType');
  const hasBothKinds = all.some(isRenewing) && all.some(u => !isRenewing(u));

  const q = search.trim().toLowerCase();
  const visible = all.filter(u => inStage(u, stage)
    && (!typeFilter || u.membershipType === typeFilter)
    && (!playerFilter || u.playerType === playerFilter)
    && (!kindFilter || (kindFilter === 'renewing') === isRenewing(u))
    && (!q || String(u.name || '').toLowerCase().includes(q) || String(u.email || '').toLowerCase().includes(q)));

  /* Only what is on screen counts as selected. Ticking somebody, then
     filtering them out of sight, must not leave them in a batch the admin
     can no longer see. */
  const chosen = visible.filter(u => selected.has(u.id));
  const allChosen = visible.length > 0 && chosen.length === visible.length;
  const filtersOn = stage !== 'all' || typeFilter || playerFilter || kindFilter || q;

  const stageCounts = Object.fromEntries(STAGES.map(s => [s.value, all.filter(u => inStage(u, s.value)).length]));

  const toggleOne = (id) => setSelected(prev => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });
  const toggleAll = () => setSelected(prev => {
    const next = new Set(prev);
    if (allChosen) visible.forEach(u => next.delete(u.id));
    else visible.forEach(u => next.add(u.id));
    return next;
  });
  const clearFilters = () => { setSearch(''); setStage('all'); setTypeFilter(''); setPlayerFilter(''); setKindFilter(''); };

  /* Applies what the server sent back, and drops anybody now complete. */
  const applyUpdates = (updated) => {
    const byId = new Map(updated.map(u => [u.id, u]));
    const done = updated.filter(u => u.complete).map(u => u.id);
    setRows(list => list
      .filter(x => !done.includes(x.id))
      .map(x => (byId.has(x.id) ? {
        ...x,
        membershipFeePaidAt: byId.get(x.id).membershipFeePaidAt,
        membershipCardIssuedAt: byId.get(x.id).membershipCardIssuedAt,
      } : x)));
    if (done.length) setSelected(prev => { const next = new Set(prev); done.forEach(id => next.delete(id)); return next; });
    /* Subs paid sets Financial on the server; tell the members list so its
       switch moves too, rather than disagreeing until somebody reloads. */
    if (onMembersChanged && updated.length) {
      onMembersChanged(Object.fromEntries(updated.map(u => [u.id, {
        membershipFeePaidAt: u.membershipFeePaidAt,
        membershipCardIssuedAt: u.membershipCardIssuedAt,
        ...(u.isFinancial !== undefined ? { isFinancial: u.isFinancial } : {}),
      }])));
    }
    return done.length;
  };

  const setFlag = async (row, field, next) => {
    setSaving(s => ({ ...s, [row.id]: true }));
    /* Move the tick straight away — a checkbox that waits for a round trip
       before it moves reads as broken on a list somebody is working down. */
    const stamp = next ? new Date().toISOString() : null;
    const key = field === 'feePaid' ? 'membershipFeePaidAt' : 'membershipCardIssuedAt';
    const before = row[key];
    setRows(list => list.map(x => (x.id === row.id ? { ...x, [key]: stamp } : x)));
    try {
      const r = await fetch(`${API}/api/users/${row.id}/membership-progress`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ [field]: next }),
      });
      const data = await r.json().catch(() => null);
      if (!r.ok || !data) throw new Error((data && data.error) || 'That did not save');
      /* Both ticked: they are a member now, and this page is for the ones who
         are not. Said out loud, because a row vanishing on its own is
         alarming rather than satisfying. */
      if (applyUpdates([{ id: row.id, ...data }])) showToast(`${row.name} is now a full member`, 'green');
    } catch (err) {
      setRows(list => list.map(x => (x.id === row.id ? { ...x, [key]: before } : x)));
      showToast(err.message || 'That did not save', 'red');
    } finally {
      setSaving(s => { const next2 = { ...s }; delete next2[row.id]; return next2; });
    }
  };

  const bulk = async (field) => {
    if (!chosen.length) return;
    setBulkBusy(true);
    try {
      const r = await fetch(`${API}/api/users/membership-progress`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ids: chosen.map(u => u.id), [field]: true, date: bulkDate || undefined }),
      });
      const data = await r.json().catch(() => null);
      if (!r.ok || !data) throw new Error((data && data.error) || 'That did not save');
      const finished = applyUpdates(data.updated || []);
      const n = (data.updated || []).length;
      const what = field === 'cardIssued' ? `${n} card${n === 1 ? '' : 's'} issued` : `${n} marked paid`;
      showToast([
        what,
        finished ? `${finished} now full member${finished === 1 ? '' : 's'}` : null,
        data.skipped ? `${data.skipped} skipped` : null,
      ].filter(Boolean).join(' · '), 'green');
    } catch (err) {
      showToast(err.message || 'That did not save', 'red');
    } finally {
      setBulkBusy(false);
    }
  };

  const columns = [
    {
      key: 'pick',
      width: 22,
      label: (
        <input type="checkbox" className="gs-check"
          aria-label={allChosen ? 'Unselect everybody shown' : 'Select everybody shown'}
          checked={allChosen}
          ref={el => { if (el) el.indeterminate = chosen.length > 0 && !allChosen; }}
          disabled={!visible.length}
          onChange={toggleAll} />
      ),
      render: u => (
        <input type="checkbox" className="gs-check"
          aria-label={`Select ${u.name}`}
          checked={selected.has(u.id)}
          onChange={() => toggleOne(u.id)} />
      ),
    },
    {
      key: 'name',
      label: 'Member',
      grow: true,
      primary: true,
      render: u => (
        <span>
          {u.name}
          <span style={{ display: 'block', fontSize: 'var(--gs-text-xs)', color: 'var(--gs-text-muted)' }}>
            {[u.membershipType, u.playerType, isRenewing(u) ? 'Renewing' : null, u.email].filter(Boolean).join(' · ') || 'No membership type'}
          </span>
        </span>
      ),
    },
    {
      key: 'applied',
      label: 'Applied',
      width: 130,
      /* createdAt as the fallback: members who applied before the form
         started recording it have no applied date of their own. */
      render: u => (
        <span style={{ color: 'var(--gs-text-muted)' }}>
          {dateish(u.membershipAppliedAt) || dateish(u.createdAt) || '—'}
        </span>
      ),
    },
    {
      key: 'approved',
      label: 'Approved',
      width: 140,
      render: (u) => {
        const waited = waitingDays(u.membershipApprovedAt);
        return (
          <span style={{ color: 'var(--gs-text-muted)' }}>
            {dateish(u.membershipApprovedAt) || '—'}
            {/* A fortnight is about when a club starts wondering whether
                anyone followed the form up. */}
            {waited !== null && waited >= LONG_WAIT_DAYS && (
              <span style={{ display: 'block', fontSize: 'var(--gs-text-xs)', color: 'var(--gs-warn, #b26a00)' }}>
                Waiting {waited} days
              </span>
            )}
          </span>
        );
      },
    },
    {
      key: 'paid',
      label: 'Subs paid',
      width: 150,
      render: u => (
        <Toggle
          ariaLabel={`Subscription paid by ${u.name}`}
          checked={!!u.membershipFeePaidAt}
          disabled={!!saving[u.id] || bulkBusy}
          label={dateish(u.membershipFeePaidAt) || 'Not paid'}
          onChange={next => setFlag(u, 'feePaid', next)} />
      ),
    },
    {
      key: 'card',
      label: 'Board approved / card issued',
      width: 220,
      render: u => (
        <Toggle
          ariaLabel={`Board approved and card issued for ${u.name}`}
          checked={!!u.membershipCardIssuedAt}
          disabled={!!saving[u.id] || bulkBusy}
          label={dateish(u.membershipCardIssuedAt) || 'Not issued'}
          onChange={next => setFlag(u, 'cardIssued', next)} />
      ),
    },
  ];

  const blockedColumns = [
    {
      key: 'name', label: 'Account', grow: true, primary: true,
      render: u => (
        <span>
          {u.name}
          <span style={{ display: 'block', fontSize: 'var(--gs-text-xs)', color: 'var(--gs-text-muted)' }}>
            {[u.membershipType, u.email].filter(Boolean).join(' · ') || 'No details'}
          </span>
        </span>
      ),
    },
    {
      key: 'lastLogin', label: 'Last signed in', width: 140,
      render: u => <span style={{ color: 'var(--gs-text-muted)' }}>{dateish(u.lastLoginAt) || 'Never'}</span>,
    },
    {
      key: 'unblock', label: '', width: 120, align: 'right',
      render: u => (
        <button type="button" className="w3-button w3-round poster-btn"
          style={{ minHeight: 'var(--gs-control-sm)' }}
          disabled={!!unblocking[u.id]}
          aria-label={`Unblock ${u.name}`}
          onClick={() => unblock(u)}>
          {unblocking[u.id] ? 'Unblocking…' : 'Unblock'}
        </button>
      ),
    },
  ];

  if (rows === null) {
    return (
      <div className="gs-panel">
        <Section label="New members"><p className="w3-text-grey">Loading…</p></Section>
      </div>
    );
  }

  return (
    <div className="gs-panel">
      <Section label="New members"
        action={
          <span style={{ fontSize: 'var(--gs-text-sm)', color: 'var(--gs-text-muted)' }}>
            {rows.length ? `${rows.length} to finish` : 'All up to date'}
          </span>
        }>
        <p className="w3-text-grey" style={{ marginTop: 0, fontSize: 'var(--gs-text-sm)' }}>
          Approved by an admin, but not members yet. Tick the subscription off when it is
          paid and the card off when the board has signed them off; once both are ticked
          the row leaves this list. A renewal form puts them back on it.
        </p>

        {error && (
          <div className="w3-panel w3-pale-red w3-leftbar w3-border-red"><p>{error}</p></div>
        )}

        {rows.length === 0 ? (
          <p className="w3-text-grey">
            Nobody is waiting. New applications appear here once an admin has approved them
            on the Members tab.
          </p>
        ) : (
          <>
            <Segmented ariaLabel="Which new members to show"
              options={STAGES.map(s => ({ ...s, label: `${s.label} (${stageCounts[s.value]})` }))}
              value={stage} onChange={setStage} style={{ marginBottom: 'var(--gs-space-3)' }} />

            {/* Only the filters this club's list can use: a menu of one
                membership type filters nothing. */}
            <div className="gs-field-grid" style={{ '--gs-cols': 'repeat(auto-fit, minmax(170px, 1fr))', marginBottom: 'var(--gs-space-3)' }}>
              <Field label="Search" htmlFor="intake-search">
                <input id="intake-search" className="w3-input" type="search"
                  placeholder="Name or email" value={search} onChange={e => setSearch(e.target.value)} />
              </Field>
              {types.length > 1 && (
                <Field label="Membership type" htmlFor="intake-type">
                  <select id="intake-type" className="w3-select" style={compactSelect}
                    value={typeFilter} onChange={e => setTypeFilter(e.target.value)}>
                    <option value="">All types</option>
                    {types.map(t => <option key={t} value={t}>{t}</option>)}
                  </select>
                </Field>
              )}
              {playerTypes.length > 1 && (
                <Field label="Player type" htmlFor="intake-player">
                  <select id="intake-player" className="w3-select" style={compactSelect}
                    value={playerFilter} onChange={e => setPlayerFilter(e.target.value)}>
                    <option value="">All player types</option>
                    {playerTypes.map(t => <option key={t} value={t}>{t}</option>)}
                  </select>
                </Field>
              )}
              {hasBothKinds && (
                <Field label="Joining or renewing" htmlFor="intake-kind">
                  <select id="intake-kind" className="w3-select" style={compactSelect}
                    value={kindFilter} onChange={e => setKindFilter(e.target.value)}>
                    <option value="">Both</option>
                    <option value="joining">New members</option>
                    <option value="renewing">Renewing</option>
                  </select>
                </Field>
              )}
            </div>

            {/* The batch. Shown only once somebody is picked, so the page
                reads as a list until there is something to do to it. */}
            {chosen.length > 0 && (
              <div className="intake-bulk" role="region" aria-label="Update the selected members">
                <strong>{chosen.length} selected</strong>
                <label className="intake-bulk-date">
                  <span>on</span>
                  <input type="date" className="w3-input" value={bulkDate}
                    max={todayInput()} onChange={e => setBulkDate(e.target.value)}
                    aria-label="Date to record" />
                </label>
                <button type="button" className="w3-button w3-round poster-btn-primary"
                  disabled={bulkBusy} onClick={() => bulk('cardIssued')}>
                  <i className="fa fa-id-card w3-margin-right" aria-hidden="true"></i>Mark card issued
                </button>
                <button type="button" className="w3-button w3-round poster-btn"
                  disabled={bulkBusy} onClick={() => bulk('feePaid')}>
                  <i className="fa fa-check w3-margin-right" aria-hidden="true"></i>Mark subs paid
                </button>
                <button type="button" className="w3-button w3-round w3-light-grey"
                  disabled={bulkBusy} onClick={() => setSelected(new Set())}>
                  Clear selection
                </button>
              </div>
            )}

            {visible.length === 0 ? (
              <p className="w3-text-grey">
                Nobody matches these filters.{' '}
                <button type="button" className="auth-forgot" onClick={clearFilters}>Show everybody</button>
              </p>
            ) : (
              <div>
                <DataRowHeader columns={columns} />
                {/* The row opens the member, the way it does on the members
                    list; the checkbox and the two switches handle their own
                    clicks. */}
                {visible.map(row => (
                  <DataRow key={row.id} columns={columns} item={row}
                    onClick={openProfile ? () => openProfile(row.id) : undefined} />
                ))}
                {filtersOn && (
                  <p className="w3-small w3-text-grey" style={{ marginBottom: 0 }}>
                    Showing {visible.length} of {rows.length}.{' '}
                    <button type="button" className="auth-forgot" onClick={clearFilters}>Clear filters</button>
                  </p>
                )}
              </div>
            )}
          </>
        )}
      </Section>

      {/* Set from the Status menu on the members list. A blocked account
          cannot sign in; listing them here is what makes that visible and
          undoable without hunting for the row. */}
      {blocked.length > 0 && (
        <Section label={`Blocked (${blocked.length})`}>
          <p className="w3-text-grey" style={{ marginTop: 0, fontSize: 'var(--gs-text-sm)' }}>
            These accounts cannot sign in. Unblocking returns them to verified if their
            membership was approved, or unverified if not.
          </p>
          <DataRowHeader columns={blockedColumns} />
          {blocked.map(u => (
            <DataRow key={u.id} columns={blockedColumns} item={u}
              onClick={openProfile ? () => openProfile(u.id) : undefined} />
          ))}
        </Section>
      )}
    </div>
  );
}

export default MembershipIntakeTab;
